import {$generateHtmlFromNodes} from '@lexical/html';
import {useLexicalComposerContext} from '@lexical/react/LexicalComposerContext';
import {LexicalEditor} from 'lexical';
import {useEffect, useRef, useState} from 'react';

/** Collab: wait for HTML to settle. Static: snapshot blobs (e.g. version diff) — minimal delay. */
export type DocumentExportPreset = 'collab' | 'static';

const TIMINGS: Record<
  DocumentExportPreset,
  {
    minWaitTime: number;
    maxWaitTime: number;
    stableCheckInterval: number;
    stableSamplesRequired: number;
  }
> = {
  collab: {
    minWaitTime: 500,
    maxWaitTime: 5000,
    stableCheckInterval: 1000,
    stableSamplesRequired: 3,
  },
  static: {
    minWaitTime: 0,
    maxWaitTime: 15000,
    stableCheckInterval: 32,
    stableSamplesRequired: 2,
  },
};

export const DocumentExportPlugin = ({
  onDocmentReady,
  preset = 'collab',
}: {
  onDocmentReady: (editor: LexicalEditor) => void;
  preset?: DocumentExportPreset;
}) => {
  const {minWaitTime, maxWaitTime, stableCheckInterval, stableSamplesRequired} = TIMINGS[preset];
  const [editor] = useLexicalComposerContext();
  const [isReady, setIsReady] = useState(false);
  const lastHtmlRef = useRef<string>('');
  const stableCountRef = useRef(0);
  const startTimeRef = useRef(Date.now());

  const pollMs = Math.max(stableCheckInterval, 4);

  useEffect(() => {
    setIsReady(false);
    lastHtmlRef.current = '';
    stableCountRef.current = 0;
    startTimeRef.current = Date.now();

    const checkStability = () => {
      const elapsedTime = Date.now() - startTimeRef.current;

      let currentHtml = '';
      editor.getEditorState().read(() => {
        currentHtml = $generateHtmlFromNodes(editor);
      });

      if (currentHtml === lastHtmlRef.current) {
        stableCountRef.current += 1;
      } else {
        stableCountRef.current = 0;
        lastHtmlRef.current = currentHtml;
      }

      if (
        (elapsedTime >= minWaitTime && stableCountRef.current >= stableSamplesRequired) ||
        elapsedTime >= maxWaitTime
      ) {
        setIsReady(true);
      }
    };

    const initialTimeout = setTimeout(checkStability, minWaitTime);
    const intervalId = setInterval(checkStability, pollMs);

    return () => {
      clearTimeout(initialTimeout);
      clearInterval(intervalId);
    };
  }, [
    editor,
    preset,
    minWaitTime,
    maxWaitTime,
    stableCheckInterval,
    stableSamplesRequired,
    pollMs,
  ]);

  useEffect(() => {
    if (isReady) {
      onDocmentReady(editor);
    }
  }, [isReady, editor, onDocmentReady]);

  return null;
};
